import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader, PrimaryBtn, OutlineBtn } from "@/components/site/shared";
import { StarPicker } from "@/components/site/StarPicker";

const track = (event) => { try { if (window.gtag) window.gtag("event", event); } catch (e) {} };
const inCls = "w-full h-12 px-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[var(--action)]";

export default function LeaveReview() {
  const [f, setF] = useState({ reviewer_name: "", reviewer_email: "", reviewer_phone: "", service_type: "", rating: 0, review_text: "", consent: false, company: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [googleUrl, setGoogleUrl] = useState("");
  const startRef = useRef(Date.now());

  useEffect(() => {
    track("review_form_opened");
    api.get("/reviews/config").then((r) => setGoogleUrl(r.data.google_leave_url || "")).catch(() => {});
  }, []);

  const upd = (k, v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.rating) { toast.error("Please select a star rating."); return; }
    if (!f.reviewer_name || !f.reviewer_email || !f.review_text) { toast.error("Please fill in your name, email and review."); return; }
    if (!f.consent) { toast.error("Please agree to display your review publicly."); return; }
    setBusy(true);
    try {
      await api.post("/reviews", { ...f, elapsed_ms: Date.now() - startRef.current });
      track("review_submitted");
      setDone(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Please try again."); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div>
        <PageHeader overline="Thank You" title="Thank You For Your Review!" subtitle="We appreciate you taking the time to share your experience." />
        <section className="section-pad">
          <div className="max-w-xl mx-auto px-4 text-center" data-testid="review-thank-you">
            <CheckCircle2 className="w-14 h-14 text-[var(--aqua)] mx-auto" />
            <p className="text-[var(--text-muted)] mt-4">Your feedback has been received and will appear on our site after a quick review.</p>
            {googleUrl && (
              <>
                <p className="mt-8 font-semibold text-[var(--navy)]">Would you also like to share your experience on Google?</p>
                <div className="flex flex-wrap justify-center gap-3 mt-4">
                  <PrimaryBtn href={googleUrl} target="_blank" rel="noopener noreferrer"
                    onClick={() => track("google_review_clicked")} data-testid="also-review-google">
                    <ExternalLink className="w-4 h-4" /> Review Us on Google
                  </PrimaryBtn>
                  <OutlineBtn to="/" data-testid="review-done">Done</OutlineBtn>
                </div>
              </>
            )}
            {!googleUrl && <div className="mt-8"><OutlineBtn to="/" data-testid="review-done">Done</OutlineBtn></div>}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <PageHeader overline="Your Experience" title="Leave a Review" subtitle="Tell us and future customers about your Veterans Power Wash experience." />
      <section className="section-pad">
        <form onSubmit={submit} className="max-w-2xl mx-auto px-4 space-y-5 bg-white border border-gray-100 rounded-2xl p-6 sm:p-8 card-shadow" data-testid="leave-review-form">
          <div>
            <label className="block text-sm font-semibold text-[var(--navy)] mb-2">Your Rating</label>
            <StarPicker value={f.rating} onChange={(v) => upd("rating", v)} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input className={inCls} placeholder="Your name" value={f.reviewer_name} onChange={(e) => upd("reviewer_name", e.target.value)} data-testid="review-name" />
            <input className={inCls} type="email" placeholder="Email (kept private)" value={f.reviewer_email} onChange={(e) => upd("reviewer_email", e.target.value)} data-testid="review-email" />
            <input className={inCls} placeholder="Phone (optional, private)" value={f.reviewer_phone} onChange={(e) => upd("reviewer_phone", e.target.value)} data-testid="review-phone" />
            <input className={inCls} placeholder="Service (optional)" value={f.service_type} onChange={(e) => upd("service_type", e.target.value)} data-testid="review-service" />
          </div>
          <textarea className={`${inCls} h-auto py-3`} rows={5} placeholder="Share your experience…" value={f.review_text} onChange={(e) => upd("review_text", e.target.value)} data-testid="review-text" />
          {/* honeypot */}
          <input type="text" name="company" value={f.company} onChange={(e) => upd("company", e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
          <label className="flex items-start gap-2 text-sm text-[var(--text-muted)]">
            <input type="checkbox" checked={f.consent} onChange={(e) => upd("consent", e.target.checked)} className="mt-1" data-testid="review-consent" />
            I agree that Veterans PWC may publish my review and display name on its website.
          </label>
          <button type="submit" disabled={busy} data-testid="submit-review"
            className="btn-lift bg-[var(--red)] hover:bg-[var(--red-dark)] text-white font-semibold rounded-full px-8 h-12 disabled:opacity-60">
            {busy ? "Submitting…" : "Submit Review"}
          </button>
        </form>
      </section>
    </div>
  );
}
